import { useState } from "react";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

export default function Login() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    if (user) {
        const dest = location.state?.from?.pathname || "/";
        return <Navigate to={dest} replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setErrorMsg("");
        setLoading(true);
        try {
            await login(email.trim().toLowerCase(), password);
            navigate(location.state?.from?.pathname || "/", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Login failed — check your connection and try again.";
            setErrorMsg(typeof detail === "string" ? detail : "Login failed.");
            toast.error(typeof detail === "string" ? detail : "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
            <div className="w-full max-w-[400px]">
                <div className="flex items-center gap-2 mb-12">
                    <img src="/haevn-mark.png" alt="HAEVN" style={{ height: 22, width: "auto" }} />
                    <span className="font-mono text-[10px] tracking-[0.24em] uppercase text-muted-foreground">
                        HAEVN Labs · Intelligence
                    </span>
                </div>
                <h1 className="font-display font-black text-3xl tracking-tight mb-2">Sign in</h1>
                <p className="editorial text-sm text-muted-foreground mb-10 leading-relaxed">
                    Access to HAEVN Labs is by invitation only. Contact your administrator if you don't have an account.
                </p>
                <form onSubmit={submit} className="space-y-5" data-testid="login-form">
                    {errorMsg && (
                        <div
                            data-testid="login-error"
                            role="alert"
                            className="border border-accent/40 bg-accent/5 text-accent rounded-sm px-3 py-2.5 text-sm leading-snug"
                        >
                            {errorMsg}
                        </div>
                    )}
                    <div>
                        <Label className="overline text-[10px] mb-1.5 block">Email</Label>
                        <Input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoFocus
                            data-testid="login-email-input"
                            placeholder="you@haevn.co"
                            className="rounded-sm"
                        />
                    </div>
                    <div>
                        <Label className="overline text-[10px] mb-1.5 block">Password</Label>
                        <Input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            data-testid="login-password-input"
                            className="rounded-sm"
                        />
                    </div>
                    <Button type="submit" disabled={loading} data-testid="login-submit-btn" className="w-full rounded-sm">
                        {loading ? <Sparkle size={14} className="mr-2 animate-pulse" /> : null}
                        {loading ? "Signing in…" : "Sign in"}
                    </Button>
                </form>
            </div>
        </div>
    );
}
